// ── Learn Loop Hook ──────────────────────────────────────────
// Client-orchestrated comprehension loop: probe → evaluate → adapt.
// Follows the useAutoRefine.js pattern.

import { useState, useCallback, useRef, useEffect } from 'react';
import { buildFlowNode, readSSEStream } from './useCanvasMode';
import { authFetch } from './api';

const API_URL = process.env.REACT_APP_API_URL || '';

export function useLearnLoop({ rawNodesRef, applyLayout, drillStackRef, dynamicTypesRef, yjsSyncRef, setNodeCount }) {
  const [isLearning, setIsLearning] = useState(false);
  const [learnProgress, setLearnProgress] = useState(null);
  // Start empty — canonical data comes from the server session via restoreLearnState.
  // Previously read from global localStorage, which caused cross-session contamination
  // when concept IDs collided (e.g. both sessions had "node_1").
  const [masteryMap, setMasteryMap] = useState({});
  const [lessonCache, setLessonCache] = useState({});
  const [paperTitle, setPaperTitle] = useState(null); // non-null when learning from a PDF paper
  // Probe style: 'text' (free-response, default) or 'mcq' (multiple-choice).
  // Toggled per-concept from LearnCard. MCQ short-circuits the AI /evaluate
  // call since correctness is computed locally against correctOptionIds.
  // Mirrored to a ref so handleStartLearn's long-lived closure reads the
  // latest value without stale-closure bugs when user toggles mode + restarts.
  const [probeMode, _setProbeMode] = useState('text');
  const probeModeRef = useRef('text');
  const setProbeMode = useCallback((mode) => {
    probeModeRef.current = mode;
    _setProbeMode(mode);
  }, []);

  // Learn state is synced to the server session (App.js sync effect).
  // No localStorage persistence — prevents cross-session contamination.

  const learnAbortRef = useRef(null);
  const isLearningRef = useRef(false);
  const masteryMapRef = useRef(masteryMap);
  const answerResolverRef = useRef(null); // resolves when user submits answer

  // Keep masteryMapRef in sync
  useEffect(() => { masteryMapRef.current = masteryMap; }, [masteryMap]);

  // Serialize raw nodes for API calls
  const serializeNodes = useCallback(() => {
    return rawNodesRef.current.map(n => ({
      id: n.id,
      type: n.data?.type || n.type,
      label: n.data?.label || n.label,
      reasoning: n.data?.reasoning || n.reasoning,
      parentIds: n.data?.parentIds || [],
      difficulty: n.data?.difficulty,
    }));
  }, [rawNodesRef]);

  // Find next concept to probe — matches left rail ordering (type → depth → difficulty)
  // Uses masteryMapRef for fresh data inside long-running async loops
  const pickNextConcept = useCallback((targetMastery, afterConceptId) => {
    const nodes = serializeNodes();
    const concepts = nodes.filter(n =>
      n.type === 'concept' || n.type === 'prerequisite'
    );

    // Build depth map via BFS from roots (matches LearnJourneyView ordering)
    const depthMap = new Map();
    const queue = [];
    nodes.forEach(n => {
      if ((n.parentIds || []).length === 0 || n.type === 'seed') {
        depthMap.set(n.id, 0);
        queue.push(n.id);
      }
    });
    while (queue.length) {
      const id = queue.shift();
      const depth = depthMap.get(id);
      nodes.forEach(n => {
        if ((n.parentIds || []).includes(id) && !depthMap.has(n.id)) {
          depthMap.set(n.id, depth + 1);
          queue.push(n.id);
        }
      });
    }

    // Sort: type priority (prerequisite < concept), then depth, then difficulty
    const typePriority = { prerequisite: 0, concept: 1 };
    concepts.sort((a, b) => {
      const ta = typePriority[a.type] ?? 1;
      const tb = typePriority[b.type] ?? 1;
      if (ta !== tb) return ta - tb;
      const da = depthMap.get(a.id) ?? 99;
      const db = depthMap.get(b.id) ?? 99;
      if (da !== db) return da - db;
      return (a.difficulty || 1) - (b.difficulty || 1);
    });

    // If afterConceptId is provided, start searching after that concept in the sorted list
    const currentMasteryMap = masteryMapRef.current;
    let startIdx = 0;
    if (afterConceptId) {
      const idx = concepts.findIndex(c => c.id === afterConceptId);
      if (idx >= 0) startIdx = idx + 1;
    }

    // Search from afterConceptId forward, then wrap around
    for (let i = 0; i < concepts.length; i++) {
      const concept = concepts[(startIdx + i) % concepts.length];
      const mastery = currentMasteryMap[concept.id]?.score || 0;
      if (mastery >= targetMastery) continue;

      const prereqsMet = (concept.parentIds || []).every(pid => {
        const parent = nodes.find(n => n.id === pid);
        if (!parent) return true;
        if (parent.type !== 'concept' && parent.type !== 'prerequisite') return true;
        return (currentMasteryMap[pid]?.score || 0) >= targetMastery;
      });

      if (prereqsMet) return concept;
    }

    return null; // all concepts mastered or blocked
  }, [serializeNodes]);

  // Check if a node is a milestone
  const findNextMilestone = useCallback((completedConceptIds) => {
    const nodes = serializeNodes();
    const milestones = nodes.filter(n => n.type === 'milestone');

    for (const milestone of milestones) {
      // Check if all parent concepts are in completedConceptIds
      const allParentsMastered = (milestone.parentIds || []).every(
        pid => completedConceptIds.has(pid)
      );
      if (allParentsMastered && !completedConceptIds.has(milestone.id)) {
        return milestone;
      }
    }
    return null;
  }, [serializeNodes]);

  // ── Main learning loop ─────────────────────────────────────
  const handleStartLearn = useCallback(async (topic, targetMastery = 7, onProgress, { startConceptId } = {}) => {
    if (isLearningRef.current) return;
    isLearningRef.current = true;
    setIsLearning(true);
    setLearnProgress(null);

    const abortController = new AbortController();
    learnAbortRef.current = abortController;

    const completedConcepts = new Set();
    const probeHistory = [];
    const completedCards = []; // collect for chat persistence
    let forcedConceptId = startConceptId || null;
    let lastConceptId = null; // track for sequential "next concept" ordering

    try {
      let iterations = 0;
      const maxIterations = 50; // safety limit

      while (iterations < maxIterations) {
        if (abortController.signal.aborted) break;
        iterations++;

        // Check for milestone
        const milestone = findNextMilestone(completedConcepts);
        if (milestone) {
          // Socratic challenge at milestone
          const socraticStatus = {
            status: 'socratic',
            milestoneId: milestone.id,
            milestoneLabel: milestone.label || milestone.reasoning,
            detail: 'Preparing milestone challenge...',
          };
          setLearnProgress(socraticStatus);
          onProgress?.(socraticStatus);

          try {
            const socRes = await authFetch(`${API_URL}/api/learn/socratic`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                nodes: serializeNodes(),
                topic,
                milestoneId: milestone.id,
                masteryMap,
              }),
              signal: abortController.signal,
            });

            if (socRes.ok) {
              const challenge = await socRes.json();
              const challengeStatus = {
                status: 'socratic',
                milestoneId: milestone.id,
                milestoneLabel: milestone.label,
                challenge: challenge.challenge,
                coveredConcepts: challenge.coveredConcepts,
                followUpQuestions: challenge.followUpQuestions,
              };
              setLearnProgress(challengeStatus);
              onProgress?.(challengeStatus);

              // Wait for user answer
              const answer = await new Promise((resolve) => {
                answerResolverRef.current = resolve;
              });
              if (abortController.signal.aborted) break;

              // Evaluate milestone answer (reuse evaluate endpoint)
              const evalRes = await authFetch(`${API_URL}/api/learn/evaluate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  nodes: serializeNodes(),
                  topic,
                  conceptId: milestone.id,
                  probe: { question: challenge.challenge, expectedInsight: challenge.expectedDepth },
                  answer,
                  currentMastery: 0,
                  ...(paperTitle ? { paperTitle } : {}),
                }),
                signal: abortController.signal,
              });

              if (evalRes.ok) {
                const evaluation = await evalRes.json();
                completedConcepts.add(milestone.id);

                const feedbackStatus = {
                  status: 'milestone_feedback',
                  milestoneId: milestone.id,
                  milestoneLabel: milestone.label,
                  mastery: evaluation.mastery,
                  feedback: evaluation.feedback,
                  correct: evaluation.correct,
                };
                setLearnProgress(feedbackStatus);
                onProgress?.(feedbackStatus);

                // Wait for user to continue
                await new Promise((resolve) => { answerResolverRef.current = resolve; });
                if (abortController.signal.aborted) break;
              }
            }
          } catch (err) {
            if (err.name === 'AbortError') break;
            console.error('Socratic challenge error:', err.message);
            completedConcepts.add(milestone.id); // skip on error
          }
          continue;
        }

        // Pick next concept (or use forced concept on first pass)
        let concept;
        if (forcedConceptId) {
          const nodes = serializeNodes();
          concept = nodes.find(n => n.id === forcedConceptId);
          forcedConceptId = null; // only force once
        }
        if (!concept) concept = pickNextConcept(targetMastery, lastConceptId);
        if (!concept) {
          // All concepts mastered!
          const completeStatus = {
            status: 'complete',
            masteryMap: { ...masteryMapRef.current },
            completedCards,
            totalConcepts: completedConcepts.size,
            detail: 'All concepts mastered!',
          };
          setLearnProgress(completeStatus);
          onProgress?.(completeStatus);
          break;
        }
        lastConceptId = concept.id;

        const currentMastery = masteryMapRef.current[concept.id]?.score || 0;

        // ── Step 0: Teach concept (skip if already partially learned) ──
        if (currentMastery < 5) {
          // Check lesson cache first — reuse previously generated lessons
          const cached = lessonCache[concept.id];
          if (cached) {
            const lessonStatus = {
              status: 'teaching',
              conceptId: concept.id,
              conceptLabel: concept.label,
              teachContent: cached,
              mastery: currentMastery,
            };
            setLearnProgress(lessonStatus);
            onProgress?.(lessonStatus);

            // Wait for user to click "Ready for Quiz"
            await new Promise((resolve) => { answerResolverRef.current = resolve; });
            if (abortController.signal.aborted) break;
          } else {
            const teachingStatus = {
              status: 'teaching',
              conceptId: concept.id,
              conceptLabel: concept.label,
              conceptReasoning: concept.reasoning,
              mastery: currentMastery,
              detail: 'Preparing lesson...',
            };
            setLearnProgress(teachingStatus);
            onProgress?.(teachingStatus);

            try {
              const teachRes = await authFetch(`${API_URL}/api/learn/teach`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  nodes: serializeNodes(),
                  topic,
                  conceptId: concept.id,
                  ...(paperTitle ? { paperTitle } : {}),
                }),
                signal: abortController.signal,
              });

              if (teachRes.ok) {
                const teachContent = await teachRes.json();
                // Cache lesson for reuse on next visit
                setLessonCache(prev => ({ ...prev, [concept.id]: teachContent }));
                const lessonStatus = {
                  status: 'teaching',
                  conceptId: concept.id,
                  conceptLabel: concept.label,
                  teachContent,
                  mastery: currentMastery,
                };
                setLearnProgress(lessonStatus);
                onProgress?.(lessonStatus);

                // Wait for user to click "Ready for Quiz"
                await new Promise((resolve) => { answerResolverRef.current = resolve; });
                if (abortController.signal.aborted) break;
              }
            } catch (err) {
              if (err.name === 'AbortError') break;
              console.warn('Teach phase failed, skipping to probe:', err.message);
            }
          }
        }

        // ── Step 1: Generate probe ────────────────────────────
        const probingStatus = {
          status: 'generating_probe',
          conceptId: concept.id,
          conceptLabel: concept.label,
          conceptReasoning: concept.reasoning,
          mastery: currentMastery,
          detail: 'Generating question...',
        };
        setLearnProgress(probingStatus);
        onProgress?.(probingStatus);

        // Route to mcq endpoint when probeMode === 'mcq'. Body shape is mostly
        // identical; mcq endpoint also accepts priorQuestions to avoid repeats.
        // Read via ref — handleStartLearn closure was created before toggle.
        const isMcq = probeModeRef.current === 'mcq';
        const probeUrl = isMcq ? '/api/learn/mcq-probe' : '/api/learn/probe';
        const probeBody = isMcq
          ? {
              nodes: serializeNodes(),
              topic,
              conceptId: concept.id,
              mastery: currentMastery,
              priorQuestions: probeHistory.filter(p => p.conceptId === concept.id).map(p => p.question),
              ...(paperTitle ? { paperTitle } : {}),
            }
          : {
              nodes: serializeNodes(),
              topic,
              conceptId: concept.id,
              mastery: currentMastery,
              priorProbes: probeHistory.filter(p => p.conceptId === concept.id),
              ...(paperTitle ? { paperTitle } : {}),
            };
        const probeRes = await authFetch(`${API_URL}${probeUrl}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(probeBody),
          signal: abortController.signal,
        });

        if (!probeRes.ok) throw new Error(`Probe error: ${probeRes.status}`);
        const probe = await probeRes.json();
        // Tag so downstream (LearnCard + evaluation step) knows the shape.
        probe._isMcq = isMcq;

        // ── Step 2: Show probe, wait for answer ───────────────
        const probeStatus = {
          status: 'probing',
          conceptId: concept.id,
          conceptLabel: concept.label,
          conceptReasoning: concept.reasoning,
          mastery: currentMastery,
          probe,
          cachedLesson: lessonCache[concept.id] || null,
        };
        setLearnProgress(probeStatus);
        onProgress?.(probeStatus);

        // Wait for user to submit answer
        const answer = await new Promise((resolve) => {
          answerResolverRef.current = resolve;
        });
        if (abortController.signal.aborted) break;

        // ── Step 3: Evaluate answer ───────────────────────────
        const evaluatingStatus = {
          status: 'evaluating',
          conceptId: concept.id,
          conceptLabel: concept.label,
          detail: 'Evaluating your answer...',
        };
        setLearnProgress(evaluatingStatus);
        onProgress?.(evaluatingStatus);

        // MCQ short-circuit: correctness is deterministic against
        // correctOptionIds, so skip the AI /evaluate call entirely. Saves
        // ~3-5s + an API hit per question. answer for MCQ is
        // { mcq: true, selectedOptionIds: [...] }.
        let evaluation;
        if (probe._isMcq && answer && typeof answer === 'object' && answer.mcq) {
          const selected = new Set(answer.selectedOptionIds || []);
          const correctSet = new Set(probe.correctOptionIds || []);
          const correct = selected.size === correctSet.size && [...correctSet].every(id => selected.has(id));
          // Delta scales by difficulty: harder Q right = bigger bump; easy Q wrong = bigger penalty.
          const diff = Math.max(1, Math.min(5, probe.difficulty || 3));
          const delta = correct ? (0.8 + diff * 0.2) : -(0.4 + (6 - diff) * 0.1);
          const newScore = Math.max(0, Math.min(10, (currentMastery || 0) + delta));
          // Build human-readable answer summary
          const correctAnswerText = (probe.options || [])
            .filter(o => correctSet.has(o.id))
            .map(o => o.text).join(probe.isMultiSelect ? ' AND ' : '');
          const wrongPicks = [...selected].filter(id => !correctSet.has(id));
          const misconceptionMsgs = wrongPicks
            .map(id => probe.distractorRationale?.[id])
            .filter(Boolean);
          evaluation = {
            mastery: newScore,
            correct,
            feedback: probe.explanation || (correct ? 'Correct.' : 'Not quite.'),
            correctAnswer: correctAnswerText,
            misconceptions: misconceptionMsgs,
            nextAction: correct ? 'continue' : 'review',
            prerequisiteGap: null,
            _mcq: true,
          };
        } else {
          const evalRes = await authFetch(`${API_URL}/api/learn/evaluate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              nodes: serializeNodes(),
              topic,
              conceptId: concept.id,
              probe,
              answer,
              currentMastery,
              ...(paperTitle ? { paperTitle } : {}),
            }),
            signal: abortController.signal,
          });
          if (!evalRes.ok) throw new Error(`Evaluate error: ${evalRes.status}`);
          evaluation = await evalRes.json();
        }

        // Update mastery map
        const newMastery = {
          score: evaluation.mastery,
          label: concept.label,
          probeCount: (masteryMap[concept.id]?.probeCount || 0) + 1,
          lastProbe: Date.now(),
        };
        setMasteryMap(prev => ({ ...prev, [concept.id]: newMastery }));

        probeHistory.push({ conceptId: concept.id, question: probe.question, mastery: evaluation.mastery });

        if (evaluation.mastery >= targetMastery) {
          completedConcepts.add(concept.id);
        }

        // ── Step 4: Show feedback ─────────────────────────────
        const feedbackStatus = {
          status: 'feedback',
          conceptId: concept.id,
          conceptLabel: concept.label,
          mastery: evaluation.mastery,
          correct: evaluation.correct,
          feedback: evaluation.feedback,
          correctAnswer: evaluation.correctAnswer,
          misconceptions: evaluation.misconceptions,
          nextAction: evaluation.nextAction,
          prerequisiteGap: evaluation.prerequisiteGap,
          cachedLesson: lessonCache[concept.id] || null,
        };
        setLearnProgress(feedbackStatus);
        onProgress?.(feedbackStatus);

        // Collect for chat history persistence
        completedCards.push({
          type: 'learn_card',
          state: { ...feedbackStatus, status: 'feedback' },
          timestamp: Date.now(),
        });

        // Wait for user to continue or request adaptation
        const continueAction = await new Promise((resolve) => {
          answerResolverRef.current = resolve;
        });
        if (abortController.signal.aborted) break;

        // ── Step 5: Adapt if needed ───────────────────────────
        if (continueAction === 'explain_differently' || evaluation.nextAction === 'explain_differently') {
          const adaptingStatus = {
            status: 'adapting',
            conceptId: concept.id,
            conceptLabel: concept.label,
            detail: 'Generating alternative explanation...',
          };
          setLearnProgress(adaptingStatus);
          onProgress?.(adaptingStatus);

          const adaptRes = await authFetch(`${API_URL}/api/learn/adapt`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              nodes: serializeNodes(),
              topic,
              conceptId: concept.id,
              evaluation,
              mastery: evaluation.mastery,
              dynamicTypes: dynamicTypesRef?.current || undefined,
            }),
            signal: abortController.signal,
          });

          if (adaptRes.ok) {
            const existingDynConfig = rawNodesRef.current[0]?.data?.dynamicConfig || null;
            const preAdaptCount = rawNodesRef.current.length;

            await readSSEStream(adaptRes, (nodeData) => {
              if (nodeData._progress) {
                setLearnProgress(prev => ({ ...prev, detail: nodeData.stage }));
                return;
              }
              if (nodeData.id && !nodeData._progress) {
                const flowNode = buildFlowNode(nodeData);
                if (existingDynConfig) flowNode.data.dynamicConfig = existingDynConfig;
                rawNodesRef.current = [...rawNodesRef.current, flowNode];
                yjsSyncRef?.current?.addNodeToYjs(flowNode);
              }
              applyLayout(rawNodesRef.current, drillStackRef?.current);
              setNodeCount?.(rawNodesRef.current.length);
            });

            // Collect adapted node content for inline summary
            const adaptedNodes = rawNodesRef.current.slice(preAdaptCount);
            const adaptSummary = adaptedNodes
              .filter(n => n.data?.reasoning)
              .map(n => `**${n.data.label}:** ${n.data.reasoning}`)
              .join('\n\n');

            if (adaptSummary) {
              const adaptDoneStatus = {
                status: 'adapt_summary',
                conceptId: concept.id,
                conceptLabel: concept.label,
                adaptSummary,
                detail: 'Here is an alternative explanation:',
              };
              setLearnProgress(adaptDoneStatus);
              onProgress?.(adaptDoneStatus);

              // Wait for user to continue before looping
              await new Promise(resolve => { answerResolverRef.current = resolve; });
              if (abortController.signal.aborted) break;
            }
          } else {
            // Brief pause on failure then continue loop
            await new Promise(r => setTimeout(r, 400));
          }
        }

        // Brief pause between concepts
        await new Promise(r => setTimeout(r, 300));
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        const errStatus = { status: 'error', error: err.message };
        setLearnProgress(errStatus);
        onProgress?.(errStatus);
      }
    } finally {
      isLearningRef.current = false;
      setIsLearning(false);
      learnAbortRef.current = null;
      applyLayout(rawNodesRef.current, drillStackRef?.current);
      onProgress?.({ status: 'done', masteryMap: { ...masteryMapRef.current }, completedCards });
    }
  }, [rawNodesRef, applyLayout, drillStackRef, dynamicTypesRef, yjsSyncRef, setNodeCount, serializeNodes, pickNextConcept, findNextMilestone, lessonCache]);

  const handleStopLearn = useCallback(() => {
    if (learnAbortRef.current) {
      learnAbortRef.current.abort();
      learnAbortRef.current = null;
    }
    // Resolve any pending answer promise
    if (answerResolverRef.current) {
      answerResolverRef.current = null;
    }
    isLearningRef.current = false;
    setIsLearning(false);
  }, []);

  const submitAnswer = useCallback((answer) => {
    if (answerResolverRef.current) {
      const resolve = answerResolverRef.current;
      answerResolverRef.current = null;
      resolve(answer);
    }
  }, []);

  const continueLoop = useCallback(() => {
    if (answerResolverRef.current) {
      const resolve = answerResolverRef.current;
      answerResolverRef.current = null;
      resolve('continue');
    }
  }, []);

  const requestExplainDifferently = useCallback(() => {
    if (answerResolverRef.current) {
      const resolve = answerResolverRef.current;
      answerResolverRef.current = null;
      resolve('explain_differently');
    }
  }, []);

  const skipConcept = useCallback((conceptId) => {
    setMasteryMap(prev => ({ ...prev, [conceptId]: { score: -1, probeCount: 0, skipped: true } }));
    if (answerResolverRef.current) {
      const resolve = answerResolverRef.current;
      answerResolverRef.current = null;
      resolve('continue');
    }
  }, []);

  const requestHint = useCallback(() => {
    // Don't resolve the answer promise — just update UI to show hint
    setLearnProgress(prev => prev ? { ...prev, showHint: (prev.showHint || 0) + 1 } : prev);
  }, []);

  // Toggle lesson review inline (doesn't resolve the answer promise)
  const reviewLesson = useCallback((conceptId) => {
    setLearnProgress(prev => {
      if (!prev) return prev;
      // Toggle: if already showing review, hide it
      if (prev._showingReview) return { ...prev, _showingReview: false };
      const cached = lessonCache[conceptId];
      if (!cached) return prev;
      return { ...prev, _showingReview: true, cachedLesson: cached };
    });
  }, [lessonCache]);

  // Restore learn state from a server session (merges with existing localStorage state)
  // Replace (not merge) — prevents cross-session contamination from stale data
  const restoreLearnState = useCallback((serverMastery, serverLessons) => {
    if (serverMastery && typeof serverMastery === 'object') {
      setMasteryMap(serverMastery);
    }
    if (serverLessons && typeof serverLessons === 'object') {
      setLessonCache(serverLessons);
    }
  }, []);

  return {
    isLearning,
    learnProgress,
    masteryMap,
    lessonCache,
    paperTitle,
    setPaperTitle,
    probeMode,
    setProbeMode,
    handleStartLearn,
    handleStopLearn,
    submitAnswer,
    continueLoop,
    requestExplainDifferently,
    skipConcept,
    requestHint,
    reviewLesson,
    restoreLearnState,
  };
}
